import React, { useEffect, useMemo, useState } from 'react';
import { useViewport } from 'reactflow';
import {
  subscribeBoardPresence,
  isFreshBoardCursor,
  type BoardCursorPresence,
} from '../../utils/boardPresenceService';

interface BoardPresenceLayerProps {
  dealId: string;
  selfUid: string;
}

/**
 * Renders peer cursors over the canvas. Positions are stored in flow
 * coordinates and projected to screen space with the current viewport, so
 * cursors stay glued to board content while panning/zooming.
 */
const BoardPresenceLayer: React.FC<BoardPresenceLayerProps> = ({ dealId, selfUid }) => {
  const { x: vx, y: vy, zoom } = useViewport();
  const [cursors, setCursors] = useState<BoardCursorPresence[]>([]);
  const [, setTick] = useState(0);

  useEffect(() => subscribeBoardPresence(dealId, selfUid, setCursors), [dealId, selfUid]);

  // Re-render periodically so cursors from closed tabs fade out even when no
  // new snapshots arrive.
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 5000);
    return () => clearInterval(t);
  }, []);

  const fresh = useMemo(() => {
    const now = Date.now();
    return cursors.filter((c) => c.surface !== 'floorplan' && isFreshBoardCursor(c, now));
  }, [cursors]);

  if (fresh.length === 0) return null;

  return (
    <div className="board-presence-layer" aria-hidden>
      {fresh.map((c) => (
        <div
          key={c.uid}
          className="board-presence-cursor"
          style={{ transform: `translate(${c.x * zoom + vx}px, ${c.y * zoom + vy}px)` }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" className="board-presence-arrow">
            <path
              d="M5.5 3.2L19 11.4l-6.3 1.5-3.2 5.8z"
              fill={c.color}
              stroke="#fff"
              strokeWidth="1.4"
            />
          </svg>
          <span className="board-presence-name" style={{ background: c.color }}>
            {c.name}
          </span>
        </div>
      ))}
    </div>
  );
};

export default React.memo(BoardPresenceLayer);
